import {Color} from '@benchling/nucleus-ui';
import {Container} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import reactUpdate from 'immutability-helper';
import React, {memo, useRef, useCallback} from 'react';
import {v4 as uuidv4} from 'uuid';

import {ReactionComponent} from 'chemistry/ChemistryConstants';
import {parseReactionComponents} from 'chemistry/ChemistryDAO';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
  CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM,
  CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import EmptySchemePlaceholder from 'notebook/integrations/chemistry/EmptySchemePlaceholder';
import ReactionSchemeContent from 'notebook/integrations/chemistry/ReactionSchemeContent';
import ReactionSketcherModal, {
  StructureWithFormat,
} from 'notebook/integrations/chemistry/ReactionSketcherModal';
import {
  StoichiometryStructuredTable,
  StoichiometryTableRow,
  StoichiometryTableCustomSlotProps,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {
  StructuredTableColumnGroups,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {BaseTypedValueType, Cell} from 'notebook/tables/TableConstants';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import {withLoadingScreen} from 'util/Promises';
import sortedBy from 'util/sortedBy';

function getMoleculeIdFromCell(cell: Cell): string | null {
  if (cell.typedValue?.type === BaseTypedValueType.BIOENTITY && cell.typedValue.bioentitySummary?.id) {
    return cell.typedValue.bioentitySummary.id;
  }
  return null;
}

function getRoleFromCell(cell: Cell): ChemistryReactionRole | null {
  const roleText = cell.text;
  if (roleText && CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM[roleText]) {
    return CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM[roleText];
  }
  return null;
}

async function handleReactionSketcherSubmit(
  mrvStructure: string,
  folderId: string,
  structuredTable: StoichiometryStructuredTable,
  updateHandler: (structuredTable: StoichiometryStructuredTable) => void
): Promise<void> {
  const {externalFile, reactants, products} = await parseReactionComponents(mrvStructure, folderId);

  const rows = mergeReactionComponentsWithExistingRows(
    reactants,
    products,
    structuredTable.rows,
    structuredTable.columns
  );

  updateHandler({
    ...structuredTable,
    metadata: {
      ...structuredTable.metadata,
      reactionMrvExternalFile: {
        id: externalFile.id,
        url: externalFile.url,
      },
    },
    rows,
  });
}

function mergeReactionComponentsWithExistingRows(
  newReactants: Array<ReactionComponent>,
  newProducts: Array<ReactionComponent>,
  existingRows: Array<StoichiometryTableRow>,
  columns: Array<StructuredTableColumn>
): Array<StoichiometryTableRow> {
  const compoundColumnIndex = columns.findIndex(
    (col) => col.columnGroup === StructuredTableColumnGroups.CHEMISTRY_MOLECULE
  );
  const roleColumnIndex = columns.findIndex(
    (col) => col.columnGroup === StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE
  );

  if (compoundColumnIndex === -1 || roleColumnIndex === -1) {
    throw new Error('Could not find required columns');
  }

  const makeRowKey = (moleculeId: string, role: ChemistryReactionRole): string => `${moleculeId}|${role}`;

  const existingRowsByKey: Record<string, StoichiometryTableRow> = {};
  const preservedRows: Array<StoichiometryTableRow> = [];

  for (const row of existingRows) {
    const moleculeId = getMoleculeIdFromCell(row.cells[compoundColumnIndex]);
    const role = getRoleFromCell(row.cells[roleColumnIndex]);

    if (moleculeId && (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT)) {
      existingRowsByKey[makeRowKey(moleculeId, role)] = row;
    } else if (moleculeId != null || role != null) {
      // Slight hack: tables initially have a single empty row but we want to remove it when populating it.
      // As a heuristic, skip any rows that have neither a moleculeId nor a role.
      preservedRows.push(row);
    }
  }

  const resultRows: Array<StoichiometryTableRow> = [];
  let rowIndex = 0;

  const processComponents = (components: Array<ReactionComponent>, role: ChemistryReactionRole): void => {
    for (const component of components) {
      const existingRow = existingRowsByKey[makeRowKey(component.moleculeId, role)];

      if (existingRow) {
        resultRows.push(existingRow);
      } else {
        const blankCells = getBlankRow(rowIndex, columns, null);
        const cells = reactUpdate(blankCells, {
          [compoundColumnIndex]: {
            text: {$set: component.name},
            typedValue: {
              $set: {
                type: BaseTypedValueType.BIOENTITY,
                bioentitySummary: {id: component.moleculeId, name: component.name},
              },
            },
          },
          [roleColumnIndex]: {
            text: {$set: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]},
            typedValue: {
              $set: {
                type: BaseTypedValueType.STRING,
                rawValue: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role],
              },
            },
          },
        });
        resultRows.push({
          cells,
          rowId: uuidv4(),
          isFinalized: false,
          metadata: {},
          lastSyncedUpstreamValues: null,
        });
      }
      rowIndex++;
    }
  };

  processComponents(newReactants, ChemistryReactionRole.REACTANT);
  processComponents(newProducts, ChemistryReactionRole.PRODUCT);

  const outputRows = resultRows.concat(preservedRows);

  return sortedBy(outputRows, [
    (row: StoichiometryTableRow): number => {
      const role = getRoleFromCell(row.cells[roleColumnIndex]);
      if (role == null) {
        // Push rows without role to the end.
        return Infinity;
      } else {
        return CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER[role];
      }
    },
  ]);
}

/**
 * A chemical sketcher rendered inside a Stoichiometry Structured Table. It can be used to populate the
 * table with reactants and products.
 */
function StoichiometryTableEmbeddedChemicalSketcher(props: StoichiometryTableCustomSlotProps): JSX.Element {
  // The sketcher is always expected to be rendered within an Entry so fileId should not be null
  const entryId = props.structuredTable.fileId!;
  const [isSketcherModalOpen, {open: openSketcherModal, close: closeSketcherModal}] = useDisclosure(false);
  const initialStructureWithFormatRef = useRef<StructureWithFormat>();
  const reactionMrvExternalFile = props.structuredTable.metadata.reactionMrvExternalFile;
  const openSketcherModalWithStructureAndFormat = useCallback(
    (structureWithFormat: StructureWithFormat) => {
      initialStructureWithFormatRef.current = structureWithFormat;
      openSketcherModal();
    },
    [openSketcherModal]
  );
  const entryFolderId = useStoreSelector(
    EntryStore,
    useCallback(
      (entryStore: EntryStore): string | undefined =>
        entryStore.getEditorState(entryId)?.entry?.folder?.api_identifier,
      [entryId]
    )
  );

  return (
    <>
      {isSketcherModalOpen && (
        <ReactionSketcherModal
          initialStructureData={initialStructureWithFormatRef.current}
          onClose={closeSketcherModal}
          onSubmit={async (newMarvinStructure: string): Promise<void> => {
            closeSketcherModal();
            await withLoadingScreen(
              handleReactionSketcherSubmit(
                newMarvinStructure,
                entryFolderId!,
                props.structuredTable,
                props.updateHandler
              )
            );
          }}
        />
      )}

      <Container
        fluid={true}
        // Not a magic number - this is design-approved but all the rendering is flexible so this can be adjusted as needed.
        h="216px"
        p="12px"
        style={{
          backgroundColor: Color.WHITE,
          border: `1px solid ${Color.GRAY_250}`,
          borderTop: 'none',
        }}
      >
        {reactionMrvExternalFile != null ? (
          <ReactionSchemeContent
            externalFileId={reactionMrvExternalFile.id}
            externalFileUrl={reactionMrvExternalFile.url}
            entryId={entryId}
            openSketcherModalWithStructureAndFormat={openSketcherModalWithStructureAndFormat}
          />
        ) : (
          <EmptySchemePlaceholder
            openSketcherModalWithStructureAndFormat={openSketcherModalWithStructureAndFormat}
          />
        )}
      </Container>
    </>
  );
}

export default memo(StoichiometryTableEmbeddedChemicalSketcher);
